import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../components-v2/Button';
import { PlusIcon2 } from '../../svgs/icons/PlusIcon';
import { IMediaTileProps, MediaTile } from './MediaTile';
import { styleSheet } from './styles';

const moveItem = <T,>(items: readonly T[], fromIndex: number, toIndex: number): T[] => {
	const moved = [...items];
	const [item] = moved.splice(fromIndex, 1);
	moved.splice(toIndex, 0, item);
	return moved;
};

const clampIndex = (index: number, length: number) => Math.min(Math.max(index, 0), length - 1);

/** A type no text field accepts, so a tile dropped on the post's text box types nothing into it. */
const TILE_DRAG_TYPE = 'application/x-levitate-media-tile';

/** Tilt on the media being carried. Free of the grid's clipping, so it can be a confident angle. */
const DRAG_GHOST_TILT_DEGREES = 4;
/** Room around the clone for the tilt's corners and the shadow, which the snapshot would otherwise cut. */
const DRAG_GHOST_GUTTER = 32;

const prefersReducedMotion = () =>
	typeof window !== 'undefined' &&
	typeof window.matchMedia === 'function' &&
	window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Tilts the media that is being carried.
 *
 * The browser rasterizes its drag image at dragstart, before React can re-render, so the tilt cannot
 * come from a class on the tile -- that would only reach the tile left behind. A clone is staged off
 * screen, tilted, and handed to `setDragImage`, which snapshots it on the spot.
 *
 * The transform sits on the clone inside the staged wrapper rather than on the wrapper itself,
 * because a transform on the element passed to `setDragImage` is not always part of the snapshot.
 */
const setTiltedDragImage = (event: React.DragEvent<HTMLElement>) => {
	const tile = event.currentTarget;
	if (typeof event.dataTransfer?.setDragImage !== 'function' || prefersReducedMotion()) {
		return;
	}

	const rect = tile.getBoundingClientRect();
	const stage = document.createElement('div');
	stage.style.cssText = `position:fixed;top:0;left:-10000px;padding:${DRAG_GHOST_GUTTER}px;pointer-events:none;`;

	const clone = tile.cloneNode(true) as HTMLElement;
	clone.style.boxSizing = 'border-box';
	clone.style.height = `${rect.height}px`;
	clone.style.margin = '0';
	clone.style.opacity = '1';
	clone.style.transform = `rotate(-${DRAG_GHOST_TILT_DEGREES}deg)`;
	clone.style.width = `${rect.width}px`;
	stage.appendChild(clone);
	document.body.appendChild(stage);

	// Keeps the media under the point it was grabbed by, offset by the room the stage adds.
	event.dataTransfer.setDragImage(
		stage,
		event.clientX - rect.left + DRAG_GHOST_GUTTER,
		event.clientY - rect.top + DRAG_GHOST_GUTTER
	);

	// The snapshot is synchronous, so the stage has done its job by the next tick.
	window.setTimeout(() => stage.remove(), 0);
};

export interface IMediaRouting {
	onToggle: (attachment: Api.IFileAttachment) => void;
	platform: Api.SocialMediaType;
	routedAttachments: Api.IFileAttachment[];
}

interface IProps
	extends Pick<
		IMediaTileProps,
		'editWithAIDisabledReason' | 'onCrop' | 'onEditWithAI' | 'onEnlarge' | 'onRemove' | 'readOnly'
	> {
	attachments: Api.IFileAttachment[];
	/** Reason the phantom tile is unavailable, or undefined while media can still be added. */
	addMediaDisabledReason?: string;
	label: string;
	/** Omitted on surfaces that cannot add media at all, such as a read-only or video-only post. */
	onAddMedia?: () => void;
	onReorder: (attachments: Api.IFileAttachment[]) => void;
	/** Set while the post is split: the tiles then also route media to the version on screen. */
	routing?: IMediaRouting;
}

/**
 * The post's whole media pool as one wrapping grid. It is the only place media appears in the
 * composer, so it carries everything that can be done to a piece of media, and the phantom tile at
 * the end is how more is added.
 *
 * Reordering is native drag and drop rather than the list library used elsewhere: that library
 * places a dragged item along a single axis and gives the wrong answer in a grid that wraps.
 * Dragging is unavailable to a keyboard, so Alt with an arrow key moves the focused tile instead.
 */
export const MediaGrid: React.FC<IProps> = ({
	addMediaDisabledReason,
	attachments,
	editWithAIDisabledReason,
	label,
	onAddMedia,
	onCrop,
	onEditWithAI,
	onEnlarge,
	onRemove,
	onReorder,
	readOnly,
	routing,
}) => {
	const keyboardHintId = React.useId();
	const routedIds = React.useMemo(
		() => new Set((routing?.routedAttachments ?? []).map(attachment => attachment.id)),
		[routing?.routedAttachments]
	);
	const [dragIndex, setDragIndex] = React.useState<number | null>(null);
	const [dropIndex, setDropIndex] = React.useState<number | null>(null);
	// A keyboard move re-inserts the tile's element, which drops focus. It goes back to the control the
	// keys came from, so a run of moves keeps working and never lands on another, such as Remove.
	const refocusRef = React.useRef<HTMLElement | null>(null);

	React.useEffect(() => {
		const control = refocusRef.current;
		refocusRef.current = null;
		control?.focus();
	}, [attachments]);

	const canReorder = !readOnly && attachments.length > 1;

	const endDrag = () => {
		setDragIndex(null);
		setDropIndex(null);
	};

	const onDragStart = (index: number) => (event: React.DragEvent<HTMLElement>) => {
		event.dataTransfer.effectAllowed = 'move';
		// Firefox starts no drag without a payload.
		event.dataTransfer.setData(TILE_DRAG_TYPE, attachments[index].id);
		setTiltedDragImage(event);
		setDragIndex(index);
	};

	const onDragOver = (index: number) => (event: React.DragEvent) => {
		// A drag that did not start on a tile — a file dragged in from the desktop, say — is left to
		// whatever else on the page wants it rather than being treated as a reorder.
		if (dragIndex === null) {
			return;
		}
		event.preventDefault();
		event.dataTransfer.dropEffect = 'move';
		setDropIndex(index);
	};

	const onDrop = (index: number) => (event: React.DragEvent) => {
		if (dragIndex === null) {
			return;
		}
		event.preventDefault();
		if (dragIndex !== index) {
			onReorder(moveItem(attachments, dragIndex, index));
		}
		endDrag();
	};

	// Fires on every move from one tile to the next as well; only leaving the grid clears the mark.
	const onGridDragLeave = (event: React.DragEvent<HTMLUListElement>) => {
		const isWithinGrid = event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget);
		if (!isWithinGrid) {
			setDropIndex(null);
		}
	};

	const onKeyDown = (index: number) => (event: React.KeyboardEvent) => {
		const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
		if (!step || !event.altKey || readOnly) {
			return;
		}
		// Alt with an arrow is also the browser's Back and Forward, so the chord is claimed even where the
		// tile has nowhere to go. Otherwise pressing it at the end of the row leaves the page.
		event.preventDefault();
		const toIndex = clampIndex(index + step, attachments.length);
		if (toIndex === index) {
			return;
		}
		refocusRef.current = event.target instanceof HTMLElement ? event.target : null;
		onReorder(moveItem(attachments, index, toIndex));
	};

	return (
		<>
			<ul
				aria-describedby={canReorder ? keyboardHintId : undefined}
				aria-label={label}
				className={css(styleSheet.grid)}
				onDragLeave={onGridDragLeave}
			>
				{attachments.map((attachment, index) => (
					<MediaTile
						attachment={attachment}
						draggable={canReorder}
						editWithAIDisabledReason={editWithAIDisabledReason}
						isDragging={dragIndex === index}
						isDropTarget={dragIndex !== null && dropIndex === index && dragIndex !== index}
						isRouted={routedIds.has(attachment.id)}
						key={attachment.id}
						onDragEnd={endDrag}
						onDragOver={onDragOver(index)}
						onDragStart={onDragStart(index)}
						onCrop={onCrop}
						onDrop={onDrop(index)}
						onEditWithAI={onEditWithAI}
						onEnlarge={onEnlarge}
						onKeyDown={onKeyDown(index)}
						onRemove={onRemove}
						onToggleRouted={routing?.onToggle}
						readOnly={readOnly}
						routingPlatform={routing?.platform}
					/>
				))}
				{onAddMedia ? (
					<li>
						<Button
							disabled={!!addMediaDisabledReason}
							onClick={onAddMedia}
							styles={[styleSheet.addTile, ...(addMediaDisabledReason ? [styleSheet.addTileDisabled] : [])]}
							title={addMediaDisabledReason}
							type='button'
							variant='ghost'
						>
							<PlusIcon2 className={css(styleSheet.addTileIcon)} />
							Add media
						</Button>
					</li>
				) : null}
			</ul>
			{canReorder ? (
				<span className={css(styleSheet.srOnly)} id={keyboardHintId}>
					Press Alt with the left or right arrow key to move the selected media.
				</span>
			) : null}
		</>
	);
};
